import { ConfigFeature } from '../../../../features/config/contract'
import { Config, DeviceDataSnapshot, platform } from '../../../../types/config'
import { ADDRESS_CH585M } from '../../protocols/address'
import {
  ensureCybercafeConfigSlotNames,
  getCybercafeConfigSlots,
  getRoleMatchedSlots,
  toFirmwareIndex,
  fromFirmwareIndex,
} from '../../../../utils/configSlots'
import { CMD_CH585M, INIT_DEFAULT_FLASH, PROTOCOL_CONFIG } from '../../protocols/cmd'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { convertASCIIToStr, convertStrToASCII } from '../../../../utils/StringUtils'
import { delay } from '../../../../utils/TimeUtils'
import { sendRead, sendWrite, checkACK } from '../../capabilities/memory'
import { getAddress } from '../../../../utils/common'
import { LayerKeysMap, Macro } from '../../../../types/keyboardKeys'
import {
  KeyboardLightingEffect,
  keyMapLighting,
  staticType,
} from '../../../../types/keyboardLighting'
import { LightingDomainType } from '../../../../types/lightingDomain'
import { LIGHTING_TARGET } from '../../../../types/lighting'
import { keysColor } from '../../../../types/color'
import { isMacOS } from '../../../../utils/envString'
import { DeviceConfigPayload, KeyTravelMap } from '../../types/CH585MConfig'
import { KeyTravelList } from '../../../../types/keyTravel'
import { formatFirmwareVersions } from '../../../../utils/firmwareVersion'
import { addrToNumber } from '../../../../cache'

const REPORT_ID = PROTOCOL_CONFIG.report_id
const FEATURE_REPORT_ID = PROTOCOL_CONFIG.feature_report_id

export class CH585MConfig extends ConfigFeature {
  async getCurrentConfig(): Promise<Config> {
    const rawIndex = await this.getCurrentConfigIndex()
    // 固件返回相对位置，转换为对外一致的绝对 slot id
    const matchedSlots = getRoleMatchedSlots(
      this.device.manifest.capabilities,
      this.device.configSlotRoles,
    )
    const index = fromFirmwareIndex(rawIndex, matchedSlots)
    const name = await this.getCurrentConfigName(index)
    this.device.currentConfig = {
      index,
      name,
    }
    return this.device.currentConfig
  }

  getCurrentConfigIndex(): Promise<number> {
    const ADDRESS = getAddress(ADDRESS_CH585M.CONFIG_CURRENT, 0)

    return sendRead(
      this.device,
      ADDRESS,
      5,
      (data) => {
        return data.getUint8(4)
      },
      [`cfg:${this.device.currentConfig.index}`],
    )
  }

  async getCurrentConfigName(index?: number): Promise<string> {
    const realIndex = index !== undefined ? index : await this.getCurrentConfigIndex()
    const ADDRESS = getAddress(ADDRESS_CH585M.CONFIG_NAME, realIndex)

    return sendRead(
      this.device,
      ADDRESS,
      0x10,
      (data) => {
        const first = data.getUint8(4)
        if (first === 0 || first === 255) {
          return ''
        }
        const array: string[] = []
        for (let i = 4; i < 20; i++) {
          const byte = data.getUint8(i)
          if (byte === 0) break
          array.push(byte.toString(16).padStart(2, '0'))
        }
        return convertASCIIToStr(array)
      },
      [`cfg:${realIndex}`],
    )
  }

  private async getConfigs(filter: boolean = true): Promise<Config[]> {
    const ADDRESS = ADDRESS_CH585M.CONFIG_NAME
    const configs: Config[] = []
    for (const [index, _] of ADDRESS.entries()) {
      const name = await this.getCurrentConfigName(index)
      if (filter && name === '') {
        continue
      }
      configs.push({
        name: name,
        index: index,
      })
    }
    return configs
  }

  async getAllConfigs(): Promise<Config[]> {
    const cafeSlots = getCybercafeConfigSlots(this.device.manifest)
    if (cafeSlots) {
      await ensureCybercafeConfigSlotNames(
        cafeSlots,
        () => this.getConfigs(false),
        (name, index) => this.createConfig(name, index),
      )
    }

    // 如果是设备有配置槽位，则不过滤全取，交由配置槽位自行过滤
    const matchedSlots = getRoleMatchedSlots(
      this.device.manifest.capabilities,
      this.device.configSlotRoles,
    )
    const res = await this.getConfigs(!matchedSlots)
    this.device.configList = res
    return this.device.configList
  }

  async setCurrentConfig(index: number): Promise<void> {
    const matchedSlots = getRoleMatchedSlots(
      this.device.manifest.capabilities,
      this.device.configSlotRoles,
    )
    const firmwareIndex = toFirmwareIndex(index, matchedSlots)
    const ADDRESS = getAddress(ADDRESS_CH585M.CURRENT_CONNECT_PROTOCOL, 0)
    /**
     * 这里读一下是为了激活设备
     * 直接写即使写入成功但是灯效等都是不生效的
     */
    const res = await sendRead(this.device, ADDRESS, 8, (data) => {
      const b0 = data.getUint8(4)
      const b1 = data.getUint8(5)
      const b2 = data.getUint8(6)
      const b3 = data.getUint8(7)
      return [b0, b1, b2, b3]
    })
    res[1] = firmwareIndex
    res[2] = firmwareIndex

    const targetConfig = this.device.configList.find((config) => config.index === index)
    if (targetConfig) {
      this.device.currentConfig = targetConfig
    }
    return sendWrite(this.device, ADDRESS, res)
  }

  private convertAndCheckConfigName(name: string): number[] {
    name = name.trim()
    if (name.length === 0) {
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, '配置名称不能为空')
    }
    const data = convertStrToASCII(name)
    if (data.length > 0x10) {
      throw new DeviceError(DeviceErrorCode.MAX_LIMIT_REACHED, '配置名称过长')
    }
    while (data.length < 0x10) {
      data.push(0)
    }
    return data
  }

  renameConfig(config: Config): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_CH585M.CONFIG_NAME, config.index)
    // 转换名字为 ASCI 码
    const nameData = this.convertAndCheckConfigName(config.name)
    return sendWrite(this.device, ADDRESS, nameData).then(() => {
      // 配置名属于配置作用域元数据,沿用 cfg 命名空间整体失效(tag-mapping §1)
      this.device.cache.invalidateByTag(`cfg:${config.index}`)
    })
  }

  async createConfig(name: string, index?: number): Promise<number> {
    name = name.trim()
    if (name.length === 0) {
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, '配置名称不能为空')
    }
    const data = this.convertAndCheckConfigName(name)
    let configIndex = index
    if (configIndex === undefined) {
      const allConfigs = await this.getConfigs(false)
      configIndex = -1
      // 找到爲空的數組按順序
      for (const config of allConfigs) {
        if (config.name === '') {
          configIndex = config.index
          break
        }
      }
      if (configIndex === -1) {
        throw new DeviceError(DeviceErrorCode.MAX_LIMIT_REACHED, '无空闲配置')
      }
    }

    const cmd = CMD_CH585M.add_config

    const connectType = this.device.connectionType
    const payload = new Uint8Array([connectType, cmd[0], configIndex, ...data])

    // 使用 BaseDevice 提供的 sendAndWait 简化流程
    const res = await this.device.sendAndWait(REPORT_ID, payload, FEATURE_REPORT_ID, (res) => {
      const ack = res.getUint8(0)
      return ack !== 0x02
    })

    // 写入成功通常 ACK 为 0x01 (或者其他非 0x02)
    checkACK(res)

    // 需要延迟500ms
    await delay(500)

    // 新增走 CMD(add_config),非 sendWrite,address-range 兜不住,按 cfg 整体失效(tag-mapping §1 场景C)
    this.device.cache.invalidateByTag(`cfg:${configIndex}`)

    return configIndex
  }

  async deleteConfig(index: number): Promise<void> {
    // 如果是当前使用的配置，需要先切换到默认配置再删除
    const currentConfig = await this.getCurrentConfig()
    if (currentConfig.index === index) {
      await this.setCurrentConfig(0)
      await delay(500)

      const switchedConfig = await this.getCurrentConfig()
      if (switchedConfig.index === index) {
        throw new DeviceError(DeviceErrorCode.TIMEOUT, '切换默认配置超时，已取消删除配置')
      }
    }

    const deleteOnce = async () => {
      const cmd = CMD_CH585M.delete_config
      const connectType = this.device.connectionType
      const payload = new Uint8Array([connectType, cmd[0], index])
      const res = await this.device.sendAndWait(
        REPORT_ID,
        payload,
        FEATURE_REPORT_ID,
        (response) => {
          const ack = response.getUint8(0)
          return ack !== 0x02
        },
      )

      checkACK(res)
      await delay(500)

      // 删除命令不是 EEPROM sendWrite，需主动失效配置名地址，确保校验读取真实硬件。
      const address = getAddress(ADDRESS_CH585M.CONFIG_NAME, index)
      this.device.cache.invalidateByAddressRange(addrToNumber(address), 0x10)
    }

    await deleteOnce()
    if ((await this.getCurrentConfigName(index)) !== '') {
      // 部分固件在刚切离当前配置时会 ACK 删除但未实际执行；仅额外重试一次，避免无限等待。
      await deleteOnce()
      if ((await this.getCurrentConfigName(index)) !== '') {
        throw new DeviceError(DeviceErrorCode.WRITE_FAILED, `删除配置 ${index} 后校验失败`)
      }
    }
  }

  private async initDefaultFlash(dafaultType: number): Promise<void> {
    const cmd = CMD_CH585M.init_default_flash

    const connectType = this.device.connectionType
    const payload = new Uint8Array([connectType, cmd[0], 0x52, dafaultType])

    const res = await this.device.sendAndWait(REPORT_ID, payload, FEATURE_REPORT_ID, (res) => {
      const ack = res.getUint8(0)
      return ack !== 0x02
    })

    checkACK(res)

    // 初始话需要主动清理缓存
    this.device.cache.clearAll()
  }

  async initAllConfigs(): Promise<void> {
    this.initDefaultFlash(INIT_DEFAULT_FLASH.factory)
  }

  /**
   * 获取当前设备的配置数据快照
   * 期望实现：
   * 收集当前型号所有功能的get方法返回数据组成快照数据
   * 注意，如果非必要，没有对应set方法的数据无需收集，因为这意味着数据无法恢复
   * @returns DeviceDataSnapshot 设备的配置数据快照
   */
  async getCurrentConfigSnapshot(): Promise<DeviceDataSnapshot> {
    const startTime = Date.now()
    const metadata = {
      deviceType: this.device.manifest.type,
      deviceModel: this.device.manifest.model,
      timestamp: startTime,
      sdkVersion: this.device.sdkVersion,
    }

    const payload = {
      configName: '',
      firmwareVersion: '',
      keyboard: {
        keysMap: [] as LayerKeysMap,
        keysMapFn: [] as LayerKeysMap,
        keysMapMac: [] as LayerKeysMap,
        keysMapMacFn: [] as LayerKeysMap,
        macroList: [] as Macro[],
      },
      lighting: {
        lightingEffect: [] as KeyboardLightingEffect[],
        currentEffect: 0,
        brightness: 60,
        speed: 60,
        customStyle: [] as keysColor[],
        customRGBKeyMap: [] as { index: number; keyMapLighting: keyMapLighting }[],
      },
      sideLighting: {
        enabled: false,
        currentEffect: 0,
        customStyle: [] as keysColor[],
      },
      travel: {
        rt: 0,
        keyTravelMap: [] as KeyTravelMap,
      },
    }

    // 环境校验
    const keyboard = this.device.features.keyboardKeys
    const lighting = this.device.features.keyboardLighting
    const travel = this.device.features.keyTravel
    const firmware = this.device.features.firmware
    if (!keyboard || !lighting || !travel || !firmware) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED_VERSION, '当前使用SDK功能不完整')
    }

    const updateProgress = (progress: number) => {
      this.device.emit('config:Upload_current_config', progress)
    }
    updateProgress(0)

    // ========== 1：轻量操作优先 (5%) ==========
    console.log('1. 基础配置')
    payload.configName = await this.getCurrentConfigName()
    payload.firmwareVersion = formatFirmwareVersions(await firmware.getFirmwareVersion())
    updateProgress(5)

    // ========== 2：灯效基础信息 (10%) -> 累计 15% ==========
    console.log('2. 灯效基础信息')
    const [lightingEffects, currentEffect] = await Promise.all([
      lighting.getEffects(),
      lighting.getCurrentEffect(),
    ])
    // 获取公共的亮度和速度
    const [brightness, speed] = await Promise.all([
      lighting.getBrightness().catch(() => 0),
      lighting.getSpeed().catch(() => 0),
    ])
    payload.lighting.lightingEffect = lightingEffects
    payload.lighting.currentEffect = currentEffect
    payload.lighting.brightness = brightness
    payload.lighting.speed = speed
    updateProgress(15)

    // ========== 3：灯效详情 (30%) -> 累计 45% ==========
    console.log('3. 灯效详情')
    const lightingDetailPromises = []
    const totalEffects = payload.lighting.lightingEffect.length

    // 包装 Promise 以追踪进度
    for (const effect of payload.lighting.lightingEffect) {
      const index = effect.id
      const effectMode = effect.kind

      const detailPromise = (async () => {
        let result = null
        if (effectMode === 'dynamic') {
          const { buffer: _buffer, ...rest } = await lighting.getConfig(
            LightingDomainType.Dynamic,
            index,
          )
          result = { type: 'dynamic', data: rest, index }
        } else if (effectMode === 'static') {
          const { buffer: _buffer, ...rest } = await lighting.getConfig(
            LightingDomainType.Static,
            index,
          )
          const keyMapLightingData = [] as keyMapLighting

          // 减少数据量，自定义的灯效才支持同步
          if (effect.isCustom) {
            if (lighting.getKeysColorStream) {
              for await (const keyRgb of lighting.getKeysColorStream(
                LightingDomainType.Static,
                index,
              )) {
                keyMapLightingData.push(keyRgb)
              }
            }
          }
          result = { type: 'static', data: rest, keyMapLighting: keyMapLightingData, index }
        }

        return result
      })()
      lightingDetailPromises.push(detailPromise)
    }

    // 批量处理灯效详情 (增加进度平滑度)
    const lightingDetails = []
    for (let i = 0; i < lightingDetailPromises.length; i++) {
      lightingDetails.push(await lightingDetailPromises[i])
      // 实时计算进度：15 (起始) + 30 * (当前完成数 / 总数)
      const currentStepProgress = 15 + Math.round((30 * (i + 1)) / totalEffects)
      updateProgress(Math.min(currentStepProgress, 45))
    }

    for (const detail of lightingDetails) {
      if (!detail) continue
      payload.lighting.customStyle.push(detail.data)
      if (detail.type === LightingDomainType.Static) {
        payload.lighting.customRGBKeyMap.push({
          index: detail.index,
          keyMapLighting: detail.keyMapLighting!,
        })
      }
    }
    // 确保到达该阶段目标值
    updateProgress(45)

    // ========== 3.5：侧灯详情 (5%) -> 累计 50% ==========
    console.log('3.5 侧灯详情')
    if (lighting.getSideLightDynamicEnable) {
      payload.sideLighting.enabled = await lighting.getSideLightDynamicEnable()
    }
    if (lighting.getSideEffect) {
      payload.sideLighting.currentEffect = await lighting.getSideEffect()
    }
    // 读取侧灯效果配置
    try {
      const sideEffects = await lighting.getEffects(LIGHTING_TARGET.Side)
      for (const effect of sideEffects) {
        try {
          const { buffer: _buffer, ...rest } = await lighting.getConfig(
            LightingDomainType.Side,
            effect.id,
          )
          payload.sideLighting.customStyle.push(rest)
        } catch {
          /* skip failed side effect */
        }
      }
    } catch {
      /* no side lighting */
    }
    updateProgress(50)

    // ========== 4：键盘映射 (20%) -> 累计 70% ==========
    console.log('4. 键盘映射')

    const BATCH_SIZE = 22 // 与 getLayerKeysMapStream 内部默认值一致
    const progressStart = 50
    const progressEnd = 65
    const progressRange = progressEnd - progressStart

    // 计算某层总 chunk 数（利用新增的 getLayerKeyCount）
    const calcTotalChunks = (isFn: boolean, platform?: platform) => {
      const keyCount = keyboard.getLayerKeyCount(isFn, platform)
      return Math.ceil(keyCount / BATCH_SIZE)
    }

    // 确定需要读取的层（mac / win）
    const layers: { isFn: boolean; platform?: platform }[] = isMacOS
      ? [
          { isFn: false, platform: 'mac' },
          { isFn: true, platform: 'mac' },
        ]
      : [{ isFn: false }, { isFn: true }]

    // 总 chunk 数（两层之和）
    let totalChunks = 0
    for (const layer of layers) {
      totalChunks += calcTotalChunks(layer.isFn, layer.platform)
    }
    let completedChunks = 0

    // 流式读取一层，并在每个 chunk 回调进度
    const readLayerKeysMapStream = async (
      isFnLayer: boolean,
      platform?: 'mac' | 'windows',
    ): Promise<LayerKeysMap> => {
      const result: LayerKeysMap = []
      if (keyboard.getLayerKeysMapStream) {
        const streamArgs: any[] = platform
          ? [isFnLayer, BATCH_SIZE, platform]
          : [isFnLayer, BATCH_SIZE]
        for await (const chunk of keyboard.getLayerKeysMapStream(...streamArgs)) {
          result.push(...chunk)
          completedChunks++
          // 按完成比例更新进度
          const progress =
            progressStart + Math.round((progressRange * completedChunks) / totalChunks)
          updateProgress(Math.min(progress, progressEnd))
        }
      }
      return result
    }

    // 按层顺序读取（mac / win 分支）
    if (isMacOS) {
      const keysMapMac = await readLayerKeysMapStream(false, 'mac')
      payload.keyboard.keysMapMac = keysMapMac
      const keysMapMacFn = await readLayerKeysMapStream(true, 'mac')
      payload.keyboard.keysMapMacFn = keysMapMacFn
    } else {
      const keysMap = await readLayerKeysMapStream(false)
      payload.keyboard.keysMap = keysMap
      const keysMapFn = await readLayerKeysMapStream(true)
      payload.keyboard.keysMapFn = keysMapFn
    }

    // ========== 5：宏数据 (20%) -> 累计 85% ==========
    console.log('5. 宏数据')
    // 第一步：流式读取宏列表
    updateProgress(70) // 读取列表中
    if (keyboard.getMacroListStream) {
      for await (const macroChunk of keyboard.getMacroListStream()) {
        payload.keyboard.macroList.push(...macroChunk)
      }
    }

    // 第二步：批量获取宏详情
    updateProgress(75) // 准备读取详情
    const macroList = payload.keyboard.macroList.filter((macro) => macro.index !== undefined)
    const totalMacros = macroList.length

    for (let i = 0; i < macroList.length; i++) {
      const macro = macroList[i]
      const data = await keyboard.getMacroByIndex(macro.index!)
      Object.assign(macro, data)

      // 进度在 75% ~ 85% 之间
      const currentStepProgress = 75 + Math.round((10 * (i + 1)) / totalMacros)
      updateProgress(Math.min(currentStepProgress, 85))
    }
    updateProgress(85)

    // ========== 6：行程 (15%) -> 累计 100% ==========
    console.log('6. 行程')
    payload.travel.rt = await travel.getGlobalRTConfig()
    updateProgress(90) // 完成全局配置

    if (travel.getKeyTravelStream) {
      // 0-2全局rt, 3自定义
      const rt = [0, 1, 2, 3]
      for (let i = 0; i < rt.length; i++) {
        const index = rt[i]
        const keyTravelItem = {
          index,
          keyTravelList: [] as KeyTravelList,
        }
        for await (const travelData of travel.getKeyTravelStream(index)) {
          keyTravelItem.keyTravelList.push(...travelData)
        }
        payload.travel.keyTravelMap.push(keyTravelItem)

        // 进度在 90% ~ 100% 之间
        const currentStepProgress = 90 + Math.round((10 * (i + 1)) / rt.length)
        updateProgress(Math.min(currentStepProgress, 100))
      }
    } else {
      updateProgress(100)
    }

    console.log(`快照读取完成，总耗时：${Date.now() - startTime}ms`)
    return { metadata, payload }
  }
  /*
   * 同步快照数据到当前配置
   */
  async setCurrentConfigSnapshot(snapshot: DeviceDataSnapshot) {
    const startTime = Date.now()
    const { metadata, payload } = snapshot as DeviceDataSnapshot<DeviceConfigPayload>

    // 环境校验
    const keyboard = this.device.features.keyboardKeys
    const lighting = this.device.features.keyboardLighting
    const travel = this.device.features.keyTravel
    if (!keyboard || !lighting || !travel) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED_VERSION, '当前使用SDK功能不完整')
    }
    // 头文件校验
    if (metadata === undefined || metadata.sdkVersion === undefined) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED_VERSION, '当前使用SDK版本不匹配')
    }
    if (metadata.deviceModel !== this.device.manifest.model) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '设备匹配失败')
    }
    if (metadata.deviceType !== this.device.manifest.type) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '设备匹配失败2')
    }
    // 数据校验
    if (payload === undefined) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '数据异常')
    }

    const updateProgress = (progress: number) => {
      this.device.emit('config:Download_current_config', progress)
    }
    // 初始进度埋点：0%
    updateProgress(0)

    // 校验完成进度埋点：5%
    updateProgress(5)

    if (payload?.configName) {
      // ========== 1: 基础配置 ==========
      console.log('1. 基础配置')
      // 软件端未去改名，这里保持一致
      // const config = {
      //   index: this.device.currentConfig.index,
      //   name: payload.configName,
      // }
      // await this.renameConfig(config)
      // 基础配置完成进度埋点：10%
      updateProgress(10)
    }

    if (payload?.lighting) {
      // ========== 2：灯效详情 ==========
      const totalEffects = payload.lighting.lightingEffect?.length || 0
      // 无灯效时直接跳进度
      if (totalEffects === 0) {
        // 灯效详情无数据进度埋点：40%
        updateProgress(40)
      } else {
        // 灯效详情开始处理进度埋点：10%
        updateProgress(10)
        for (let i = 0; i < payload.lighting.lightingEffect.length; i++) {
          const effect = payload.lighting.lightingEffect[i]
          const index = effect.id
          const effectMode = effect.kind
          // 查询对应的customStyle的键盘快照数据
          const style = payload.lighting.customStyle.find((style) => style.index === index)
          if (!style) {
            // 跳过无效灯效时更新进度
            const currentProgress = 10 + Math.round((30 * (i + 1)) / totalEffects)
            updateProgress(Math.min(currentProgress, 40))
            continue
          }
          // 动态：设置颜色（炫彩或指定颜色） + 亮度 + 速度
          if (effectMode === 'dynamic') {
            await lighting.setConfig(LightingDomainType.Dynamic, index, {
              brightness: style.brightness ?? 60,
              speed: style.speed ?? 60,
              isColorful: style.isColorful,
              rgb: style.rgb,
            })
          }
          // 静态：设置渐变或指定颜色 + 亮度
          if (effectMode === 'static') {
            if (style.gradientDirection !== undefined) {
              const domain = LightingDomainType.Static
              if (effect.isCustom) {
                // 用户自定义静态效果，用 createCustomEffect 创建
                await lighting.createCustomEffect(
                  {
                    name: effect.i18nKey,
                    staticType: style.gradientDirection as staticType,
                    brightness: style.brightness ?? 60,
                    rgbGroup: style.rgbGroup,
                  },
                  index,
                )
                // 查询对应的customRGBKeyMap的键盘快照数据
                const customRGBKeyMap = payload.lighting.customRGBKeyMap.find(
                  (item) => item.index === index,
                )
                if (customRGBKeyMap) {
                  await lighting.setKeysColor(domain, customRGBKeyMap.keyMapLighting, index)
                }
              } else {
                // 1 无渐变 2 上下渐变 3 左右渐变
                if (style.gradientDirection === 1) {
                  await lighting.setProperty(domain, index, 'brightness', style.brightness ?? 60)
                }
                if ([2, 3].includes(style.gradientDirection) && style.rgbGroup) {
                  await lighting.setConfig(domain, index, {
                    gradientDirection: style.gradientDirection,
                    brightness: style.brightness ?? 60,
                    rgbGroup: style.rgbGroup,
                  })
                }
              }
            }
          }
          // 灯效详情分步进度埋点
          const currentProgress = 10 + Math.round((30 * (i + 1)) / totalEffects)
          updateProgress(Math.min(currentProgress, 40))
        }
      }

      // ========== 3：灯效基础信息 ==========
      console.log('3. 灯效基础信息')
      await lighting.setProperty(
        LightingDomainType.Dynamic,
        payload.lighting.currentEffect,
        'brightness',
        payload.lighting.brightness ?? 60,
      )
      await lighting.setProperty(
        LightingDomainType.Dynamic,
        payload.lighting.currentEffect,
        'speed',
        payload.lighting.speed ?? 60,
      )
      await lighting.setCurrentEffect(payload.lighting.currentEffect ?? 100)
      // 灯效基础信息完成进度埋点：45%
      updateProgress(45)
    } else {
      // 无灯效配置进度埋点：45%
      updateProgress(45)
    }

    // ========== 3.5：侧灯配置 ==========
    if (payload?.sideLighting) {
      if (lighting.setSideLightDynamicEnable) {
        await lighting.setSideLightDynamicEnable(payload.sideLighting.enabled)
      }
      // 恢复侧灯效果配置
      for (const style of payload.sideLighting.customStyle) {
        await lighting.setConfig(LightingDomainType.Side, style.index!, {
          brightness: style.brightness,
          speed: style.speed,
          isColorful: style.isColorful,
          rgb: style.rgb,
        })
      }
      // 恢复当前侧灯效果
      if (lighting.setSideEffect && payload.sideLighting.currentEffect !== undefined) {
        await lighting.setSideEffect(payload.sideLighting.currentEffect)
      }
    }

    if (payload?.keyboard) {
      // ========== 4：键盘映射 ==========
      console.log('4. 键盘映射')
      const platform = isMacOS ? 'mac' : 'win'
      const keyMap = isMacOS ? payload.keyboard.keysMapMac : payload.keyboard.keysMap
      const fnKeyMap = isMacOS ? payload.keyboard.keysMapMacFn : payload.keyboard.keysMapFn

      // 写入普通层
      if (keyMap && keyMap.length > 0) {
        const totalKeyMap = keyMap.length
        for (let i = 0; i < totalKeyMap; i++) {
          await keyboard.setLayerKeysMap(keyMap[i], false, platform)
          // 键盘普通层分步进度埋点
          const currentProgress = 45 + Math.round((10 * (i + 1)) / totalKeyMap)
          updateProgress(Math.min(currentProgress, 55))
        }
      } else {
        // 无普通层映射进度埋点：55%
        updateProgress(55)
      }

      // 写入FN层
      if (fnKeyMap && fnKeyMap.length > 0) {
        const totalFnKeyMap = fnKeyMap.length
        for (let i = 0; i < totalFnKeyMap; i++) {
          await keyboard.setLayerKeysMap(fnKeyMap[i], true, platform)
          // 键盘FN层分步进度埋点
          const currentProgress = 55 + Math.round((10 * (i + 1)) / totalFnKeyMap)
          updateProgress(Math.min(currentProgress, 65))
        }
      } else {
        // 无FN层映射进度埋点：65%
        updateProgress(65)
      }
    } else {
      // 无键盘配置进度埋点：65%
      updateProgress(65)
    }

    if (payload?.keyboard?.macroList) {
      // ========== 5：宏数据 ==========
      console.log('5. 宏数据')
      const macroList = payload.keyboard.macroList.filter((m) => m.index !== undefined)
      const totalMacros = macroList.length

      if (totalMacros === 0) {
        // 无宏数据进度埋点：85%
        updateProgress(85)
      } else {
        // 宏数据开始处理进度埋点：70%
        updateProgress(70)
        for (let i = 0; i < totalMacros; i++) {
          const macro = macroList[i]
          await keyboard.setMacroByIndex(macro, macro.index ?? 0)
          // 宏数据分步进度埋点
          const currentProgress = 70 + Math.round((15 * (i + 1)) / totalMacros)
          updateProgress(Math.min(currentProgress, 85))
        }
      }
    } else {
      // 无宏配置进度埋点：85%
      updateProgress(85)
    }

    if (payload?.travel) {
      // ========== 6：行程配置 ==========
      console.log('6. 行程配置')
      const keyTravelMap = payload.travel.keyTravelMap
      const totalTravelItems = keyTravelMap?.length || 0

      // 写入按键行程
      if (totalTravelItems > 0) {
        for (let i = 0; i < totalTravelItems; i++) {
          const keyItem = keyTravelMap[i]
          await travel.setKeyTravel(keyItem.keyTravelList, keyItem.index)
          // 行程配置分步进度埋点
          const currentProgress = 85 + Math.round((10 * (i + 1)) / totalTravelItems)
          updateProgress(Math.min(currentProgress, 95))
        }
      } else {
        // 无按键行程进度埋点：95%
        updateProgress(95)
      }

      // 写入全局RT配置
      await travel.setGlobalRTConfig(payload.travel.rt)
      // 行程配置完成进度埋点：100%
      updateProgress(100)
    } else {
      // 无行程配置进度埋点：100%
      updateProgress(100)
    }

    console.log(`快照应用完成，总耗时：${Date.now() - startTime}ms`)
    // 整组配置替换,按 cfg 整体失效最稳妥(tag-mapping §1 批量)
    this.device.cache.invalidateByTag(`cfg:${this.device.currentConfig.index}`)
  }

  /*************** 以下是管理员模式使用的接口 ********************/
  // 恢复管理员设置
  async initAllConfigsByAdmin(): Promise<void> {
    this.initDefaultFlash(INIT_DEFAULT_FLASH.admin)
  }

  // 切换管理员模式，1 开启 0 关闭
  private async switchAdminMode(mode: number): Promise<void> {
    const cmd = mode ? CMD_CH585M.enter_admin_mode : CMD_CH585M.exit_admin_mode

    const connectType = this.device.connectionType
    const payload = new Uint8Array([connectType, ...cmd])

    const res = await this.device.sendAndWait(REPORT_ID, payload, FEATURE_REPORT_ID, (res) => {
      const ack = res.getUint8(0)
      return ack !== 0x02
    })

    checkACK(res)
    this.device.adminMode = mode === 1
  }

  // 进入管理员模式
  async enterAdminMode(): Promise<void> {
    this.switchAdminMode(1)
  }

  // 退出管理员模式
  async exitAdminMode(): Promise<void> {
    this.switchAdminMode(0)
  }
}
